import { useEffect, useRef } from 'react';
import { matchPath, Outlet, useLocation, useMatches } from 'react-router';

import { AnalyticsPageNames, trackPageOptOutRoutes } from 'constants/analytics';
import useAuth from 'hooks/useAuth';
import useGlobalStore from 'hooks/useGlobalStore';
import useNanoBannerAnalyticsProperties from 'hooks/useNanoBannerAnalyticsProperties';
import SEO from 'shared/SEO';
import { trackPage } from 'utils/analyticsUtils';
import { capitalize } from 'utils/globalUtils';

function AnalyticsLayout() {
    const location = useLocation();
    const trackedRef = useRef('');
    const matches = useMatches();
    const routeMetadata = matches?.at(-1)?.handle?.meta;
    let routePageTitle = routeMetadata?.pageTitle;
    const { isMisfitsTheme } = useGlobalStore();
    const { authStatus } = useAuth();
    const isLoggedIn = authStatus === 'authenticated';
    const nanoBannerProperties = useNanoBannerAnalyticsProperties();

    /**
     * Page view tracking here is opt-out (if your route is nested in this layout).
     * Useful for page view events that require page-level data and therefore implement their own tracking.
     */
    const fireTrackPage = !trackPageOptOutRoutes.some((route) => matchPath(route, location.pathname));

    if (routeMetadata?.pageTitle === 'Aisle') {
        const pageTitle = matches?.at(-1)?.params?.category;
        const capitalizedRoutePageTitle = pageTitle
            .split('+')
            .map((word) => capitalize(word))
            .join(' ');
        routePageTitle = capitalizedRoutePageTitle;
    }

    // track page event on URL change (wait for auth so is_logged_in is accurate)
    useEffect(() => {
        if (authStatus === 'configuring') return;
        if (trackedRef.current === location.pathname) return;

        if (fireTrackPage) {
            const extraProperties = {
                'page name': routeMetadata?.pageName,
                ...nanoBannerProperties,
            };

            if (routeMetadata?.pageName === AnalyticsPageNames.AISLE) {
                extraProperties.aisle = routePageTitle;
            }

            trackPage({
                extraProperties,
                isLoggedIn,
                isMisfitsTheme,
                pathname: location.pathname,
                search: location.search,
            });
        }

        trackedRef.current = location.pathname;
    }, [
        location.pathname,
        location.search,
        authStatus,
        isLoggedIn,
        isMisfitsTheme,
        fireTrackPage,
        routeMetadata?.pageName,
        routePageTitle,
        nanoBannerProperties,
    ]);

    return (
        <>
            {routePageTitle && (
                <SEO
                    canonicalURL={`${window.location.origin}${location.pathname}`}
                    title={routePageTitle}
                    description={routeMetadata?.pageDescription}
                />
            )}
            <Outlet />
        </>
    );
}

export default AnalyticsLayout;
